import { useEffect, useMemo, useState } from "react";
import { api, formatCurrency, monthNames } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, BarChart3, LineChart as LineIcon } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";

const COLORS = ["#C05621", "#3182CE", "#38A169", "#D69E2E", "#805AD5", "#E53E3E", "#319795", "#DD6B20"];

export const CommissionsChart = ({ sellers = [] }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [sellerId, setSellerId] = useState("all");
  const [chartType, setChartType] = useState("bar");
  const [loading, setLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const results = await Promise.all(
          Array.from({ length: 12 }, (_, i) =>
            api.getSellerCommissions({ year, month: i + 1 }).then((r) => r.data)
          )
        );
        const rows = results.map((r, i) => {
          const row = { month: monthNames[i], total: 0 };
          (r.commissions || []).forEach((c) => {
            row[c.seller_name] = (row[c.seller_name] || 0) + (c.payable_commission || 0);
            row.total += c.payable_commission || 0;
          });
          return row;
        });
        setMonthlyData(rows);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, sellers]);

  const displayedSellers = useMemo(() => {
    if (sellerId === "all") {
      const names = new Set();
      monthlyData.forEach((row) =>
        Object.keys(row).forEach((k) => {
          if (k !== "month" && k !== "total") names.add(k);
        })
      );
      return Array.from(names);
    }
    const s = sellers.find((x) => x.id === sellerId);
    return s ? [s.name] : [];
  }, [sellerId, sellers, monthlyData]);

  const totalYear = monthlyData.reduce((s, r) => {
    if (sellerId === "all") return s + (r.total || 0);
    const name = sellers.find((x) => x.id === sellerId)?.name;
    return s + (name ? (r[name] || 0) : 0);
  }, 0);

  const years = [new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1];

  return (
    <Card data-testid="commissions-chart-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Évolution annuelle des commissions
          </CardTitle>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[100px]" data-testid="chart-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sellerId} onValueChange={setSellerId}>
              <SelectTrigger className="w-[200px]" data-testid="chart-seller">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les vendeurs</SelectItem>
                {sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setChartType((t) => (t === "bar" ? "line" : "bar"))}
              data-testid="chart-type-toggle"
              title={chartType === "bar" ? "Passer en ligne" : "Passer en barres"}
            >
              {chartType === "bar" ? <LineIcon className="h-4 w-4" /> : <BarChart3 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground mb-3">
          Total {year}
          {sellerId !== "all" ? ` (${sellers.find((s) => s.id === sellerId)?.name || ""})` : ""} :
          <strong className="text-foreground ml-1 currency">{formatCurrency(totalYear)}</strong>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            {chartType === "bar" ? (
              <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                {sellerId === "all" ? (
                  displayedSellers.map((name, i) => (
                    <Bar key={name} dataKey={name} stackId="a" fill={COLORS[i % COLORS.length]} />
                  ))
                ) : (
                  <Bar dataKey={sellers.find((s) => s.id === sellerId)?.name || "total"} fill={COLORS[0]} />
                )}
              </BarChart>
            ) : (
              <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                {sellerId === "all" ? (
                  displayedSellers.map((name, i) => (
                    <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} />
                  ))
                ) : (
                  <Line type="monotone" dataKey={sellers.find((s) => s.id === sellerId)?.name || "total"} stroke={COLORS[0]} strokeWidth={2} />
                )}
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default CommissionsChart;
